/**
 * Pinned Admin Home section — always on, never hideable.
 * Governance exception requests awaiting review + pending invitations. A part
 * that could not be read says Unavailable; it never reads as "nothing here".
 * Gate approvals (ADMIN-GATES-ATTENTION-1) are classified server-side with the
 * engine's own eligibility check; see AdminHomeGatesSection.
 */
import { Link } from "react-router-dom";

import { gatesAttentionIsEmpty } from "../gatesAttention";

import { AdminHomeGatesSection } from "./AdminHomeGatesSection";
import { ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF } from "./AdminHomePrimaryActions";

import { Card, CardBody, CardHeader } from "@/components/ui/card/Card";
import { typography } from "@/design";
import type {
  GatesAttention,
  GovernanceDecision,
  OrgInviteListItem,
} from "@/features/administration/api/administration.api";
import { cn } from "@/lib/utils";

const PEOPLE_HREF = "/administration/people";
const TOP_ITEMS = 5;

export type AdminHomeNeedsAttentionProps = {
  decisions: GovernanceDecision[] | null;
  decisionsTotal: number | null;
  invites: OrgInviteListItem[] | null;
  loading: boolean;
  /** The pending-exceptions read failed: say so, do not show an empty queue. */
  decisionsUnavailable?: boolean;
  /** The pending-invitations read failed. */
  invitesUnavailable?: boolean;
  /**
   * Gate approvals for the signed-in admin. `undefined` = this surface does not
   * show gates; `null` = not read yet (or failed — see `gatesUnavailable`).
   */
  gates?: GatesAttention | null;
  /** The gates read failed: say Unavailable, never an empty list. */
  gatesUnavailable?: boolean;
};

function decisionLabel(d: GovernanceDecision): string {
  const place = d.projectName?.trim() || d.workspaceName?.trim() || "Organization";
  const kind = d.type.replace(/_EXCEPTION$/i, "").replace(/_/g, " ");
  return `${place} · ${kind}`;
}

export function AdminHomeNeedsAttention({
  decisions,
  decisionsTotal,
  invites,
  loading,
  decisionsUnavailable = false,
  invitesUnavailable = false,
  gates,
  gatesUnavailable = false,
}: AdminHomeNeedsAttentionProps) {
  const decisionRows = (decisions ?? []).slice(0, TOP_ITEMS);
  const inviteRows = (invites ?? []).slice(0, TOP_ITEMS);
  const decisionCount =
    decisionsTotal ?? (decisions === null ? null : decisionRows.length);
  const inviteCount = invites === null ? null : invites.length;
  // Avoid a blank body while a second load sets loading=true but keeps prior [].
  const gatesShown = gates !== undefined;
  const showLoading =
    loading &&
    (decisions === null ||
      invites === null ||
      decisionCount === null ||
      (gatesShown && gates === null && !gatesUnavailable));
  const empty =
    !showLoading &&
    !decisionsUnavailable &&
    !invitesUnavailable &&
    (decisionCount ?? 0) === 0 &&
    (inviteCount ?? 0) === 0 &&
    // Only a gates read that SUCCEEDED and found nothing can say "nothing".
    (!gatesShown || (!gatesUnavailable && gates !== null && gatesAttentionIsEmpty(gates)));

  return (
    <Card data-testid="admin-home-needs-attention">
      <CardHeader className="space-y-1 border-b border-slate-100 p-5">
        <h2 className={typography.sectionTitle}>Needs your attention</h2>
        <p className={typography.muted}>
          Exception requests awaiting review and invitations still pending.
        </p>
      </CardHeader>
      <CardBody className={cn("space-y-4 p-5")}>
        {showLoading ? (
          <p className={typography.muted}>Loading…</p>
        ) : null}

        {empty ? (
          <p
            className={typography.muted}
            data-testid="admin-home-needs-attention-empty"
          >
            Nothing needs your attention right now
          </p>
        ) : null}

        {!showLoading && gatesShown ? (
          <AdminHomeGatesSection
            gates={gates ?? null}
            unavailable={gatesUnavailable}
          />
        ) : null}

        {!showLoading && decisionsUnavailable ? (
          <p
            className={typography.muted}
            data-testid="admin-home-needs-attention-decisions-unavailable"
          >
            Exception requests: Unavailable
          </p>
        ) : null}

        {!showLoading && invitesUnavailable ? (
          <p
            className={typography.muted}
            data-testid="admin-home-needs-attention-invites-unavailable"
          >
            Pending invitations: Unavailable
          </p>
        ) : null}

        {!showLoading && !decisionsUnavailable && (decisionCount ?? 0) > 0 ? (
          <div data-testid="admin-home-needs-attention-decisions">
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <h3 className={cn(typography.label, "text-slate-800")}>
                Exception requests awaiting review
              </h3>
              <span
                className="text-sm font-semibold tabular-nums text-slate-900"
                data-testid="admin-home-needs-attention-decisions-count"
              >
                {decisionCount}
              </span>
            </div>
            <ul className="space-y-1.5">
              {decisionRows.map((d) => (
                <li key={d.id}>
                  <Link
                    to={ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF}
                    className="block rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                    data-testid={`admin-home-needs-attention-decision-${d.id}`}
                  >
                    {decisionLabel(d)}
                  </Link>
                </li>
              ))}
            </ul>
            {(decisionCount ?? 0) > decisionRows.length ? (
              <Link
                to={ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF}
                className={cn(
                  "mt-2 inline-block",
                  typography.label,
                  "text-blue-700 hover:text-blue-800",
                )}
              >
                Review all
              </Link>
            ) : null}
          </div>
        ) : null}

        {!showLoading && !invitesUnavailable && (inviteCount ?? 0) > 0 ? (
          <div data-testid="admin-home-needs-attention-invites">
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <h3 className={cn(typography.label, "text-slate-800")}>
                Pending invitations
              </h3>
              <span
                className="text-sm font-semibold tabular-nums text-slate-900"
                data-testid="admin-home-needs-attention-invites-count"
              >
                {inviteCount}
              </span>
            </div>
            <ul className="space-y-1.5">
              {inviteRows.map((inv) => (
                <li key={inv.id}>
                  <Link
                    to={PEOPLE_HREF}
                    className="block rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                    data-testid={`admin-home-needs-attention-invite-${inv.id}`}
                  >
                    {inv.email}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
